import { useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown, FileText, X } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  useInvoicePaymentAmounts,
  useInvoicePaymentMethods,
  useInvoicesList,
  formatMoney,
  type InvoiceListFilters,
  type InvoiceListItem,
} from '@/hooks/useInvoices';
import { usePatientsByIds } from '@/hooks/usePatientPicker';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FilterChip, StatusChip } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { PageLayout } from '@/components/ui/PageLayout';
import { ResponsiveTable, type TableColumn } from '@/components/ui/Table';
import { Skeleton } from '@/components/ui/Skeleton';
import { TYPE, UI_MUTED } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { computeInvoiceKpis } from '@/pages/economy/invoiceKpis';
import { FINANCE_NUM, formatFinanceAmount, formatFinanceDate, formatFinanceMonth } from '@/pages/economy/financeFormat';
import { InvoiceFiltersPopover } from './InvoiceFiltersPopover';
import { InvoiceStatusBadges } from './invoiceStatus';
import {
  EMPTY_INVOICE_FILTERS,
  activeInvoiceStatusTab,
  countActiveInvoiceFilterGroups,
  invoiceFiltersFromStatusTab,
  invoiceFiltersToSearchParams,
  invoiceForfaldSortValue,
  invoiceSignedTotalCents,
  invoiceStatusSortLabel,
  matchesInvoiceFilters,
  parseInvoiceFiltersFromSearch,
  resolveInvoiceDateRange,
  toInvoiceListQueryFilters,
  type InvoicePageFilters,
  type InvoiceStatusTab,
} from './invoiceFilters';

export { InvoiceStatusBadges, StatusBadge } from './invoiceStatus';

const STATUSES: InvoiceStatusTab[] = [
  'all',
  'draft',
  'issued',
  'overdue',
  'partial',
  'paid',
  'cancelled',
  'refunded',
  'void',
  'credit_note',
];

type SortColumn = 'invoice_number' | 'patient' | 'status' | 'total' | 'issued_at' | 'due_at';
type SortDirection = 'asc' | 'desc';

function compareNullableDate(a: string | null, b: string | null): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return new Date(a).getTime() - new Date(b).getTime();
}

function sortInvoices(
  rows: InvoiceListItem[],
  column: SortColumn,
  direction: SortDirection,
): InvoiceListItem[] {
  const sorted = [...rows];
  const dir = direction === 'asc' ? 1 : -1;

  sorted.sort((a, b) => {
    let cmp = 0;
    switch (column) {
      case 'invoice_number':
        cmp = (a.invoice_number ?? '').localeCompare(b.invoice_number ?? '', 'da', {
          numeric: true,
        });
        break;
      case 'patient': {
        const aName = `${a.patient_last_name ?? ''} ${a.patient_first_name ?? ''}`.trim();
        const bName = `${b.patient_last_name ?? ''} ${b.patient_first_name ?? ''}`.trim();
        cmp = aName.localeCompare(bName, 'da');
        break;
      }
      case 'status':
        cmp = invoiceStatusSortLabel(a).localeCompare(invoiceStatusSortLabel(b), 'da');
        break;
      case 'total':
        cmp = invoiceSignedTotalCents(a) - invoiceSignedTotalCents(b);
        break;
      case 'issued_at':
        cmp = compareNullableDate(a.issued_at, b.issued_at);
        break;
      case 'due_at':
        cmp = compareNullableDate(invoiceForfaldSortValue(a), invoiceForfaldSortValue(b));
        break;
      default: {
        const unexpected: never = column;
        return unexpected;
      }
    }
    return cmp * dir;
  });

  return sorted;
}

export function Invoices({ embedded = false }: { embedded?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageFilters = useMemo(() => parseInvoiceFiltersFromSearch(searchParams), [searchParams]);
  // Snapshot "now" when URL filters change so overdue does not flicker during sort/render.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pageFilters is the intentional invalidation key
  const now = useMemo(() => new Date(), [pageFilters]);
  const activeStatusTab = activeInvoiceStatusTab(pageFilters);
  const activeFilterCount = countActiveInvoiceFilterGroups(pageFilters, now);

  const [sortColumn, setSortColumn] = useState<SortColumn>('issued_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const writeFilters = (next: InvoicePageFilters, replace = false) => {
    setSearchParams(invoiceFiltersToSearchParams(next), { replace });
  };

  const queryFilters = useMemo<InvoiceListFilters>(
    () => toInvoiceListQueryFilters(pageFilters, now),
    [pageFilters, now],
  );

  const listQuery = useInvoicesList(activeClinicId, queryFilters);
  const kpiQuery = useInvoicesList(activeClinicId, {});
  const paymentMethodsQ = useInvoicePaymentMethods(activeClinicId);
  const paymentAmountsQ = useInvoicePaymentAmounts(activeClinicId);
  const selectedPatientsQ = usePatientsByIds(activeClinicId, pageFilters.patientIds);

  const { data, isLoading, error, refetch } = listQuery;

  const patientLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const patient of selectedPatientsQ.data ?? []) {
      labels[patient.id] = `${patient.first_name} ${patient.last_name}`.trim();
    }
    for (const invoice of data ?? []) {
      if (!labels[invoice.patient_id]) {
        labels[invoice.patient_id] =
          `${invoice.patient_first_name ?? ''} ${invoice.patient_last_name ?? ''}`.trim();
      }
    }
    return labels;
  }, [selectedPatientsQ.data, data]);

  const invoices = useMemo(() => {
    return (data ?? []).filter((inv) =>
      matchesInvoiceFilters(inv, pageFilters, now, paymentMethodsQ.data?.byInvoiceId, inv.id),
    );
  }, [data, pageFilters, now, paymentMethodsQ.data]);

  const sortedInvoices = useMemo(
    () => sortInvoices(invoices, sortColumn, sortDirection),
    [invoices, sortColumn, sortDirection],
  );

  const kpis = useMemo(
    () => computeInvoiceKpis(kpiQuery.data ?? [], now, paymentAmountsQ.data),
    [kpiQuery.data, now, paymentAmountsQ.data],
  );

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === 'invoice_number' || column === 'patient' ? 'asc' : 'desc');
  };

  const sortHeader = (column: SortColumn, label: string) => {
    const active = sortColumn === column;
    const SortIcon = active ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <button
        type="button"
        onClick={() => handleSort(column)}
        className="inline-flex items-center gap-s1 hover:text-ds-teal"
        aria-label={
          active
            ? sortDirection === 'asc'
              ? t('invoices.sort_asc')
              : t('invoices.sort_desc')
            : undefined
        }
      >
        {label}
        <SortIcon className={cn('h-3.5 w-3.5', active ? 'text-ds-teal' : 'text-ds-muted')} />
      </button>
    );
  };

  const columns: TableColumn<InvoiceListItem>[] = [
    {
      id: 'number',
      header: sortHeader('invoice_number', t('invoices.col_number')),
      cell: (inv) => <InvoiceNumberCell invoice={inv} />,
    },
    {
      id: 'patient',
      header: sortHeader('patient', t('invoices.col_patient')),
      cell: (inv) => (
        <span className="font-medium">
          {inv.patient_first_name} {inv.patient_last_name}
        </span>
      ),
    },
    {
      id: 'status',
      header: sortHeader('status', t('invoices.col_status')),
      cell: (inv) => (
        <div>
          {inv.kind === 'credit_note' ? (
            <StatusChip tone="coral">{t('invoices.kind_credit_note')}</StatusChip>
          ) : (
          <InvoiceStatusBadges
            status={inv.status}
            lastSentAt={inv.last_sent_at}
            lastReminderSentAt={inv.last_reminder_sent_at}
            paymentMethod={inv.payment_method}
            totalPaidCents={inv.total_paid_cents}
          />
          )}
          {inv.status === 'partial' ? (
            <div className={cn('mt-s1', UI_MUTED, FINANCE_NUM)}>
              {formatFinanceAmount(inv.total_paid_cents, i18n.language)} /{' '}
              {formatFinanceAmount(inv.total_cents, i18n.language)}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      id: 'total',
      header: sortHeader('total', t('invoices.col_total')),
      numeric: true,
      cell: (inv) => (
        <span className={cn(FINANCE_NUM, 'font-semibold')}>
          {formatFinanceAmount(inv.kind === 'credit_note' ? -Math.abs(inv.total_cents) : inv.total_cents, i18n.language)}
        </span>
      ),
    },
    {
      id: 'issued',
      header: sortHeader('issued_at', t('invoices.col_issued_at')),
      cell: (inv) => (
        <span className={cn(FINANCE_NUM, 'whitespace-nowrap')}>
          {formatFinanceDate(inv.issued_at, i18n.language) ?? (
            <span className="text-ds-muted">—</span>
          )}
        </span>
      ),
    },
    {
      id: 'due',
      header: sortHeader('due_at', t('invoices.col_due_at')),
      cell: (inv) => {
        if (inv.kind === 'credit_note') {
          return <span className="text-ds-muted">—</span>;
        }
        if (inv.status === 'paid') {
          const paidOn = formatFinanceDate(inv.paid_at ?? inv.last_payment_at, i18n.language);
          return (
            <span className={cn(FINANCE_NUM, 'whitespace-nowrap')}>
              {paidOn ? t('invoices.paid_on', { date: paidOn }) : t('invoices.paid_at_label')}
            </span>
          );
        }
        const due = formatFinanceDate(inv.due_at, i18n.language);
        const overdue =
          inv.status === 'overdue' ||
          (inv.status === 'issued' && inv.due_at != null && new Date(inv.due_at) < new Date());
        return (
          <span className={cn(FINANCE_NUM, 'whitespace-nowrap', overdue && 'text-ds-red-ink')}>
            {due ?? <span className="text-ds-muted">{t('invoices.no_due_date')}</span>}
          </span>
        );
      },
    },
  ];

  const newInvoiceButton = (
    <Button onClick={() => navigate('/invoices/new')} disabled={!activeClinicId}>
      {t('invoices.new_invoice')}
    </Button>
  );

  const monthLabel = formatFinanceMonth(now, i18n.language);
  const kpiReady =
    !activeClinicId || (kpiQuery.isFetchedAfterMount && paymentAmountsQ.isFetchedAfterMount);
  const kpiCards = !kpiReady ? (
    <div className="grid grid-cols-2 gap-s3 nav:grid-cols-4 nav:gap-s4" aria-busy>
      {Array.from({ length: 4 }, (_, index) => (
        <Skeleton key={index} className="h-24 w-full" />
      ))}
    </div>
  ) : (
    <div className="grid grid-cols-2 gap-s3 nav:grid-cols-4 nav:gap-s4">
      <KpiCard
        label={t('invoices.kpi_issued_month', { month: monthLabel })}
        value={formatFinanceAmount(kpis.issuedCents, i18n.language)}
        hint={t('invoices.kpi_count', { count: kpis.issuedCount })}
      />
      <KpiCard
        label={t('invoices.kpi_outstanding')}
        value={formatFinanceAmount(kpis.outstandingCents, i18n.language)}
        hint={t('invoices.kpi_count', { count: kpis.outstandingCount })}
      />
      <KpiCard
        label={t('invoices.kpi_overdue_amount')}
        value={formatFinanceAmount(kpis.overdueCents, i18n.language)}
        hint={t('invoices.kpi_count', { count: kpis.overdueCount })}
        danger
      />
      <KpiCard
        label={t('invoices.kpi_paid_month', { month: monthLabel })}
        value={formatFinanceAmount(kpis.paidCents, i18n.language)}
        hint={t('invoices.kpi_count', { count: kpis.paidCount })}
      />
    </div>
  );

  const headingRow = (
    <div className="flex w-full min-w-0 flex-wrap items-start justify-between gap-s3 nav:flex-nowrap nav:items-center">
      {embedded ? null : (
        <div className="shrink-0">
          <h1 className={cn(TYPE.title, 'text-ds-text')}>{t('invoices.title')}</h1>
        </div>
      )}
      <div className="w-full min-w-0 max-w-search nav:w-[420px] nav:min-w-[12.5rem] nav:flex-1">
        <Input
          value={pageFilters.search}
          onChange={(e) => writeFilters({ ...pageFilters, search: e.target.value }, true)}
          placeholder={t('invoices.search_placeholder')}
          aria-label={t('invoices.search_placeholder')}
        />
      </div>
      <div className="flex flex-wrap items-center gap-s3 nav:shrink-0">
        <div className="flex flex-wrap items-center gap-s2" role="group" aria-label={t('invoices.filter_status')}>
          {STATUSES.map((s) => (
            <FilterChip
              key={s}
              pressed={activeStatusTab === s}
              onClick={() =>
                writeFilters(invoiceFiltersFromStatusTab(s, pageFilters))
              }
            >
              {s === 'credit_note'
                ? t('invoices.kind_credit_note')
                : s === 'overdue'
                  ? t('invoices.status_overdue')
                  : t(`invoices.status_${s === 'all' ? 'all' : s}`)}
            </FilterChip>
          ))}
        </div>
        <InvoiceFiltersPopover
          clinicId={activeClinicId}
          filters={pageFilters}
          patientLabels={patientLabels}
          paymentMethods={paymentMethodsQ.data?.methods ?? []}
          onApply={(next) => writeFilters(next)}
        />
        {newInvoiceButton}
        <ActiveFilterChips filters={pageFilters} patientLabels={patientLabels} onChange={writeFilters} />
      </div>
    </div>
  );

  const listBody = isLoading ? (
    <Card>
      <div className="flex flex-col gap-s3" aria-busy>
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-10 w-full" />
        ))}
      </div>
    </Card>
  ) : error ? (
    <Banner
      tone="error"
      action={
        <Button type="button" variant="secondary" size="sm" onClick={() => void refetch()}>
          {t('app.retry')}
        </Button>
      }
    >
      {t('invoices.load_error')}
    </Banner>
  ) : sortedInvoices.length === 0 ? (
    <Card>
      <EmptyState
        icon={<FileText className="h-7 w-7" />}
        title={
          pageFilters.search || activeFilterCount > 0
            ? t('invoices.empty_search_title')
            : t('invoices.empty_title')
        }
        action={pageFilters.search || activeFilterCount > 0 ? undefined : newInvoiceButton}
      />
    </Card>
  ) : (
    <Card className="p-0 nav:p-0">
      <ResponsiveTable
        columns={columns}
        rows={sortedInvoices}
        getRowKey={(inv) => inv.id}
        getRowHref={(inv) => `/invoices/${inv.id}`}
        getRowLabel={(inv) =>
          `${inv.patient_first_name ?? ''} ${inv.patient_last_name ?? ''}`.trim() ||
          inv.invoice_number ||
          t('invoices.no_number')
        }
        empty={null}
        mobileCard={(inv) => (
          <div className="w-full text-left">
            <InvoiceNumberCell invoice={inv} />
            <div className={cn(TYPE.small, FINANCE_NUM, 'mt-s1 text-ds-muted')}>
              {inv.patient_first_name} {inv.patient_last_name} ·{' '}
              {formatFinanceAmount(invoiceSignedTotalCents(inv), i18n.language)}
            </div>
            <div className="mt-s2">
              {inv.kind === 'credit_note' ? (
                <StatusChip tone="coral">{t('invoices.kind_credit_note')}</StatusChip>
              ) : (
                <InvoiceStatusBadges
                  status={inv.status}
                  lastSentAt={inv.last_sent_at}
                  lastReminderSentAt={inv.last_reminder_sent_at}
                  paymentMethod={inv.payment_method}
                  totalPaidCents={inv.total_paid_cents}
                />
              )}
            </div>
          </div>
        )}
      />
    </Card>
  );

  const content = (
    <>
      {headingRow}
      <div className="mt-s5">{kpiCards}</div>
      <div className="mt-s5">{listBody}</div>
    </>
  );

  if (embedded) return content;
  return <PageLayout>{content}</PageLayout>;
}

function KpiCard({
  label,
  value,
  hint,
  danger,
}: {
  label: string;
  value: string;
  hint: string;
  danger?: boolean;
}) {
  return (
    <article className="rounded-card bg-ds-card p-s4 shadow-card">
      <div className={cn(UI_MUTED)}>{label}</div>
      <div
        className={cn(
          FINANCE_NUM,
          'mt-s1 text-[24px] font-semibold leading-8',
          danger ? 'text-ds-red-ink' : 'text-ds-text',
        )}
      >
        {value}
      </div>
      <div className={cn(UI_MUTED, FINANCE_NUM, 'mt-s1')}>{hint}</div>
    </article>
  );
}

function InvoiceNumberCell({ invoice }: { invoice: InvoiceListItem }) {
  const { t } = useTranslation();
  const href = `/invoices/${invoice.id}`;
  return (
    <Link
      to={href}
      tabIndex={-1}
      onClick={stopRowInteractive}
      className={cn(TYPE.body, FINANCE_NUM, 'block font-medium text-ds-text')}
    >
      {invoice.invoice_number ?? t('invoices.no_number')}
    </Link>
  );
}

function stopRowInteractive(event: MouseEvent) {
  event.stopPropagation();
}

function ActiveFilterChips({
  filters,
  patientLabels,
  onChange,
}: {
  filters: InvoicePageFilters;
  patientLabels: Record<string, string>;
  onChange: (next: InvoicePageFilters) => void;
}) {
  const { t } = useTranslation();
  const chips: Array<{ key: string; label: string; clear: () => InvoicePageFilters }> = [];

  if (filters.statuses.length > 0) {
    chips.push({
      key: 'status',
      label: t('invoices.filters_chip_status', {
        value: filters.statuses.map((status) => t(`invoices.status_${status}`)).join(', '),
      }),
      clear: () => ({ ...filters, statuses: [] }),
    });
  }
  if (filters.patientIds.length > 0) {
    chips.push({
      key: 'patient',
      label: t('invoices.filters_chip_patient', {
        value: filters.patientIds.map((id) => patientLabels[id] || id).join(', '),
      }),
      clear: () => ({ ...filters, patientIds: [] }),
    });
  }
  if (resolveInvoiceDateRange(filters, new Date())) {
    const rangeLabel =
      filters.datePreset === 'custom' && (filters.customFrom || filters.customTo)
        ? `${filters.customFrom ?? '…'}–${filters.customTo ?? '…'}`
        : t(`invoices.filters_date_${filters.datePreset}`);
    chips.push({
      key: 'date',
      label: t('invoices.filters_chip_date', {
        field: t(`invoices.filters_date_${filters.dateField}`),
        value: rangeLabel,
      }),
      clear: () => ({
        ...filters,
        datePreset: null,
        customFrom: null,
        customTo: null,
      }),
    });
  }
  if (filters.amountMinCents != null || filters.amountMaxCents != null) {
    const min =
      filters.amountMinCents != null
        ? formatMoney(filters.amountMinCents)
        : t('invoices.filters_amount_min');
    const max =
      filters.amountMaxCents != null
        ? formatMoney(filters.amountMaxCents)
        : t('invoices.filters_amount_max');
    chips.push({
      key: 'amount',
      label: t('invoices.filters_chip_amount', { value: `${min} – ${max}` }),
      clear: () => ({ ...filters, amountMinCents: null, amountMaxCents: null }),
    });
  }
  if (filters.paymentMethods.length > 0) {
    chips.push({
      key: 'method',
      label: t('invoices.filters_chip_method', { value: filters.paymentMethods.join(', ') }),
      clear: () => ({ ...filters, paymentMethods: [] }),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-s1">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex h-6 items-center gap-s1 rounded-full bg-ds-teal-tint px-s2 text-ds-teal"
        >
          {chip.label}
          <button
            type="button"
            onClick={() => onChange(chip.clear())}
            aria-label={t('invoices.filters_remove')}
            className="rounded-full hover:bg-ds-teal-line"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <Button
        type="button"
        variant="tertiary"
        size="sm"
        onClick={() => onChange({ ...EMPTY_INVOICE_FILTERS, search: filters.search })}
      >
        {t('invoices.filters_clear_all')}
      </Button>
    </div>
  );
}
